import { Component, OnInit, ViewChild, ElementRef, Renderer2, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, Input } from '@angular/core';
import { Store, Select, Actions, ofActionDispatched, actionMatcher, ofActionCompleted } from '@ngxs/store';
import { 
  SetReady, 
  SetEngineChapterInit,
  SynchronizeVisibleNodes,
  SetEngineChapter, 
  ClickNode, 
  FlyToObject,
  SynchronizeRenderableNodes,
  PlayAnimation,
  ShowScrollAlert
  } from '../../state/engine.actions';
import { EngineState } from '../../state/engine.state';
import { Observable, Subscription, fromEvent, BehaviorSubject, combineLatest, Subject, interval, merge } from 'rxjs';
import { filter, takeUntil, bufferTime, debounceTime, throttle, pairwise } from 'rxjs/operators';
import { EngineService } from '../../engine.service';
import { environment } from 'src/environments/environment';
import { EnginePositionModel } from '../../models/engine-position.model';
import { CameraService } from '../../camera.service';
import { EngineVisibilityEnum } from '../../models/engine-visibility.enum';
import { ViewTreeNode } from '../../models/view.tree-node';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { HitModel } from '../../models/hit.model';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { ChapterInitModel } from '../../models/engine-chapter-init-model';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { BdGraph, BdGraphStateMap } from 'src/lib/graph/graph.model';
import { PaintService } from '../../paint.service';
import { SetCamera, MoveCamera, PanCameraStatic, RotateCameraStatic, ZoomCamera, ZoomCameraStatic } from '../../state/camera/engine.camera.actions';
import { CameraMotionModel } from '../../models/camera-motion.model';
import { EngineCameraState } from '../../state/camera/engine.camera.state';
import { LoadProgressModel } from '../../models/load-progress.model';
import { AnimationService } from '../../animation.service';
import { BackgroundModel } from '../../models/background.model';
import { HoverNode, PaintNodes, UnpaintNodes } from '../../state/nodes/engine.nodes.actions';
import { EngineNodesState } from '../../state/nodes/engine.nodes.state';
import { get, isEmpty } from 'lodash';
import * as Hammer from 'hammerjs';
import { CameraModel } from '../../models/camera.model';
import { EventsEnum } from '../../models/events.enum';
import { StoryState } from 'src/app/story/state/story.state';
import { SetProgress } from '../../state/progress/engine.progress.actions';
import { LoadStoryChunkSuccess } from 'src/app/story/state/story.actions';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { PaintPresetFade } from '../../models/paint-presets.const';
import { ScrollModeEnum } from '../../models/scroll-mode.enum';
import { disableXrayMode } from 'src/app/shared/xray-mode';
import { EnvironmentApplication } from 'src/environments/models/environment-application';


declare const Human: any;

enum KeyboardCamera {
  Pan = 0.4, 
  PanSlow = 0.04,
  Zoom = 5,
  ZoomSlow = 1,
  Rotate = 0.3,
  RotateSlow = 0.03
}

enum MouseCamera {
  Rotate = 6,
  Pan = 1.75,
  Zoom = 2
}

enum TouchCamera {
  Zoom = -12,
  Pan = 2.5,
  Rotate = 1.5,
  Pinch = 1.5
}

enum CursorClasses {
  Hide = 'engine--cursor-hide',
  Label = 'engine--cursor-label',
  Paint = 'engine--cursor-paint',
  Crosshair = 'engine--cursor-crosshair',
  Default = ''
}

enum MouseButtons {
  None = 0,
  Left = 1,
  Right = 2,
  LeftAndRight = 4
}

const zoomVelocity = 10;

@Component({
  selector: 'app-engine',
  templateUrl: './engine.component.html',
  styleUrls: ['./engine.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EngineComponent implements OnInit, OnDestroy {
  mouseMove$: Observable<MouseEvent>;
  
  nodeSelectedInterceptor: Subscription;

  flyCamera = true;
  chapterActivating = false;
  visible = false;

  viewGraph: BdGraph<ViewTreeNode>;

  cursorClass: CursorClasses = CursorClasses.Default;
  
  private mouseDelta = null;

  private ngUnsubscribe = new Subject();

  @ViewChild('engine', { static: true }) engineElement: ElementRef;
  @ViewChild('engineCanvas', { static: true }) engineCanvasElement: ElementRef;
  @ViewChild('engineAnnotations', { static: true }) engineAnnotationsElement: ElementRef;


  
  // Engine
  @Select(EngineState.getReady)
  ready$: Observable<boolean>;

  @Select(EngineState.getViewGraph)
  viewGraph$: Observable<BdGraph<ViewTreeNode>>;

  @Select(EngineState.getEnginePosition)
  enginePosition$: Observable<EnginePositionModel>;

  @Select(EngineNodesState.getHoveredNode)
  hoveredNode$: Observable<ViewTreeNode>;
  hoveredNode: ViewTreeNode = null;

  @Select(EngineState.getChapter)
  chapter$: Observable<string>;

  @Select(EngineState.getChapterInit)
  chapterInit$: Observable<ChapterInitModel>;


  // unused.  marked for deletion
  // @Select(EngineNodesState.getSelectedNode)
  // selectedNode$: Observable<ViewTreeNode>;

  @Select(EngineNodesState.getCheckedNodes)
  checkedNodes$: Observable<ViewTreeNode[]>;
  
  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Select(EngineState.getDisplay)
  engineDisplay$: Observable<boolean>;
  display = false;

  @Select(EngineState.getVisible)
  engineVisible$: Observable<boolean>;
  engineVisible = true;
  
  // Story - Chapters
  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;


  // Camera
  @Select(EngineCameraState.getCameraMotion)
  cameraMotion$: Observable<CameraMotionModel>;

  // Background color
  @Select(StoryChaptersState.getSelectedChapterBackground)
  background$: Observable<BackgroundModel>;

  background: BackgroundModel;

  get isTransparentBackground(): boolean {
    if (this.background) {
      return BackgroundModel.isTransparent(this.background);
    } else {
      return false;
    }
  }

  get isBackgroundCheckered(): boolean {
    return environment.application !== EnvironmentApplication.Viewer && this.isTransparentBackground;
  }


  ctrlPressed = false;
  scrollType: 'wheel' | 'trackpad' = null;
  scrollTimeout = null;
  zoomVelocity = 0;

  @Input()
  inputEnabled = true;


  @HostListener('wheel', ['$event'])
  wheel(event: WheelEvent) {
    
    const scrollMode = this.store.selectSnapshot(EngineState.getScrollMode);
    const ctrlActive = this.ctrlPressed;

    if (scrollMode === ScrollModeEnum.DoNotCaptureShowMessage && !ctrlActive) {
      this.store.dispatch(new ShowScrollAlert(true));
    }

    if (scrollMode !== ScrollModeEnum.Capture && !ctrlActive) {
      return;
    }

    // if we're allowed to scroll, we never want the parent window to scroll
    event.preventDefault();
    this.handleWheel(event);
  }

  @HostListener('contextmenu', ['$event'])
  contextmenu(event: MouseEvent) {
    this.handleContextMenu(event);
  }

  @HostListener('mouseout', ['$event'])
  mouseout() {
    this.handleMouseOut();
  }

  constructor(
    private actions: Actions,
    private animationService: AnimationService,
    private cameraService: CameraService,
    private changeDetecton: ChangeDetectorRef,
    private engineEventsService: EngineService,
    private paintService: PaintService,
    private renderer: Renderer2,
    private store: Store,
    private graphService: BdGraphService
  ) {
  }

  // map single property state map to graph state map
  private convertToLeafStateMap(property: string, propertyMap: {[key: string]: boolean} ): BdGraphStateMap {
    const leafStateMap: BdGraphStateMap = {};
    Object.entries(propertyMap).forEach(([id, state]) => {
      leafStateMap[id] = {};
      leafStateMap[id][property] = state;
    
    });
    return leafStateMap;
  }

  ngOnInit() {
    this.bindEngineEvents();
    this.bindApplicationEvents();
    this.bindUserEvents();
    this.bindKeyEvents();
    this.bindHumanInteractions();


    this.renderer.listen(window, 'keydown', (e) => {
      if (e.key === 'Control') {
        this.ctrlPressed = true;
      }
    });

    this.renderer.listen(window, 'keyup', (e) => {
      if (e.key === 'Control') {
        this.ctrlPressed = false;
      }
    });

    Human.properties.set({assetDomain: environment.assetDomain});
    Human.workers.setPath('/assets/engine/studio/workers');
    Human.init.init();
  }

  ngOnDestroy() {
    this.changeDetecton.detach();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initEventListeners() {
    
  }

  bindEngineEvents() {
    // Graph has initialized
    this.engineEventsService.getEvent(EventsEnum.Loaded)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.store.dispatch(new SetReady(true));
      });
      
    // Chapters have started activating
    this.engineEventsService.getEvent(EventsEnum.TimelineChaptersActivateStart)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.chapterActivating = true;
      });

    // Chapters have finished
    this.engineEventsService.getEvent(EventsEnum.TimelineChaptersActivateFinish)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        this.chapterActivating = false;

        const currentBranch = Human.timeline.activeRoot._nowBranch;
        const chapterInit = {
          id: currentBranch.id,
          init: true,
          camera: currentBranch.initializedCamera,      
          annotations: currentBranch.initializedAnnotations,
          graph: currentBranch.initializedGraph,
          properties: currentBranch.initializedProperties,
          particles: currentBranch.initializedParticles,
          clips: currentBranch.initializedClippingPlanes,
          postprocessing: currentBranch.initializedPostprocess,
          transparency: currentBranch.initializedTransparencyMode
        };
        this.store.dispatch(new SetEngineChapter(params.chapterId));
        this.store.dispatch(new SetEngineChapterInit(chapterInit));
      });

    // Handle engine tick, but only when it's visible
    this.engineEventsService.getEvent(EventsEnum.Tick)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (this.visible) {
          if (this.inputEnabled) {
            this.handleMouseInputEngineTick();
          }
          this.handleCameraTick();
        }
      });

    // Handle object visibility event
    this.engineEventsService.getEvent(EventsEnum.SceneObjectsShown)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params: any) => {
        if (!this.chapterActivating) {
          const stateMap = this.convertToLeafStateMap('shown', params.enabledObjectsUpdate);
          this.store.dispatch(new SynchronizeVisibleNodes(stateMap));
        }
      });

    // Handle object renderable event
    this.engineEventsService.getEvent(EventsEnum.SceneObjectRenderable)
      .pipe(
        bufferTime(1000),
        takeUntil(this.ngUnsubscribe))
      .subscribe((renderable: Array<any> ) => {
        if (renderable.length) {
          const leafStateMap = {};
          renderable.forEach(renderableData => {
            leafStateMap[renderableData.objectId] = {'renderable': true};
          });
          this.store.dispatch(new SynchronizeRenderableNodes(leafStateMap));
        }
      });


    // Handle Loading
    this.engineEventsService
      .getEvent(EventsEnum.AssetsLoadProgress)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        throttle(() => interval(250))
        )
      .subscribe((progress: LoadProgressModel) => {

        const totalChunks = this.store.selectSnapshot(StoryState.getLoadChunks).length;
        const remainingChunks = this.store.selectSnapshot(StoryState.getLoadChunksRemaining).length;
        const currentChunkIdx = totalChunks - remainingChunks;

        const { receivedAssets, requestedAssets } = progress;
        const currentChunkProgress = receivedAssets / requestedAssets;

        const chunkWidth = 1 / totalChunks;
        const baseProgress = currentChunkIdx * chunkWidth;
        const currentProgress = currentChunkProgress * chunkWidth;

        const storyLoadProgress = currentProgress + baseProgress;
        this.store.dispatch(new SetProgress(storyLoadProgress, currentChunkProgress));
      });

  // There are story load chunks that don't actually initiate any downloading.
  // Therefore, we can only know when loading has completed by pipeing LoadStoryChunkSuccess, and there are zero chunks remaining
  this.actions
    .pipe(
      ofActionCompleted(LoadStoryChunkSuccess),
      takeUntil(this.ngUnsubscribe)
    )
    .subscribe(() => {
      const chunksRemaining = this.store.selectSnapshot(
        StoryState.getLoadChunksRemaining
      ).length;
  
      if (chunksRemaining === 0) {
        this.store.dispatch(new SetProgress(1, 1));
      }
    });
    
    // timeline: playing
    this.engineEventsService.getEvent(EventsEnum.TimelinePlayed)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new PlayAnimation(true));
      });

    // timeline: stopped
    merge(
      this.engineEventsService.getEvent(EventsEnum.TimelinePaused),
      this.engineEventsService.getEvent(EventsEnum.TimelineStopped)
    )
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new PlayAnimation(false));
      });
  }


  private highlightXrayMode(checkedNodes: ViewTreeNode[]) {

    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const uncheckedNodeIds = this.graphService.getInverseSelection(viewGraph, checkedNodes.map(node => node.id));
    const uncheckedNodes = this.graphService.getNodes(viewGraph, uncheckedNodeIds);

    uncheckedNodes.forEach(
      node => this.paintService.paint(node, PaintPresetFade.recipe)
    );  

    this.store.dispatch(new UnpaintNodes(checkedNodes));
  }

  private highlightDefaultMode(checkedNodes: ViewTreeNode[], uncheckedNodes: ViewTreeNode[]) {
    this.paintService.highlightAll(
      uncheckedNodes,
      false
    );

    this.paintService.highlightAll(
      checkedNodes,
      true
    );
  }

  bindApplicationEvents() {
    // Available screen space for the engine has changed
    this.enginePosition$
      .pipe(
        filter(position => position != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((position) => {
        this.repositionEngine(position);
      });

    this.checkedNodes$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        pairwise()
      )
      .subscribe((nodes) => {
        const [previouslySelected, currentlySelected] = nodes;

        if (this.store.selectSnapshot(EngineState.getXrayModeEnabled)) {
          this.highlightXrayMode(currentlySelected);
        } else {
          this.highlightDefaultMode(currentlySelected, previouslySelected);
        }

        // notify selection updates
        const selectedObjectsUpdate = this.calculateSelectedObjectsUpdate(previouslySelected || [], currentlySelected || []);

        // only fire the event if there is an updated
        if (!isEmpty(selectedObjectsUpdate)) {
          this.engineEventsService.fireEvent(EventsEnum.SceneObjectsSelected, { selectedObjectsUpdate });
        }
      });

    this.store.select(EngineState.getXrayModeEnabled)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(xrayModeEnabled => {

      // get checked and unchecked nodes.
      const checkedNodes = this.store.selectSnapshot(EngineNodesState.getCheckedNodes);

      // when toggling xray mode,
      // we have to refresh the way objects are presented, based on the currently selected nodes.
      if (xrayModeEnabled === false) {

        // re-paint leaf nodes
        this.store.selectSnapshot(EngineState.getXrayCachedColorRecipes).forEach(
          recipe => this.paintService.colorObject(recipe.objectId, recipe.recipe)
        );

        // re-highlight checked nodes
        this.highlightDefaultMode(checkedNodes, []);

      } else { // xray mode is enabled
        
        // turn off the highlighting for the checked nodes
        this.paintService.highlightAll(checkedNodes, false);

        // highlight xray mode for checked nodes.
        this.highlightXrayMode(checkedNodes);

      }
    });

    
    this.actions.pipe(
        ofActionDispatched(SetCamera),
        takeUntil(this.ngUnsubscribe)
      ).subscribe(
        (action: SetCamera) => 
          action.animate
          ? this.flyToPoint(action.camera, action.animationDuration)
          : this.jumpToPoint(action.camera)
      );


    this.actions.pipe(
      ofActionDispatched(PanCameraStatic),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: PanCameraStatic) => {
      this.cameraService.panCameraStatic(action.x, action.y, action.z);
    });

    this.actions.pipe(
      ofActionDispatched(RotateCameraStatic),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: RotateCameraStatic) => {
      this.cameraService.rotateCameraStatic(action.x, action.y);
    });

    this.actions.pipe(
      ofActionDispatched(ZoomCameraStatic),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: ZoomCameraStatic) => {
      this.cameraService.setHumanZoomFactor(action.value);
    });

    this.actions.pipe(
      ofActionDispatched(FlyToObject),
      filter((action: FlyToObject) => !!action.objectId),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: FlyToObject) => {
      action.animate
      ? this.flyToObject(action.objectId, action.animationDuration)
      : this.jumpToObject(action.objectId);
    });

    // New chapter
    this.selectedChapter$
      .pipe(
        filter((chapter) => {
          return chapter != null;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((chapter) => {
        // xray mode needs to be disabled when changing chapter
        disableXrayMode(this.store, this.engineEventsService);
        // set background
        this.background = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
        Human.timeline.scrub({ chapterId: chapter.id });
      });

    this.background$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((background) => {
        this.background = background;
      });
    
    
    // Selected tool changed
    this.activeTool$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((tool) => {
        // Reset hover class
        Object.values(CursorClasses).forEach(cursorClass => {
          if (cursorClass) {
            this.renderer.removeClass(this.engineElement.nativeElement, cursorClass);
          }
        });

        switch (tool) {
          case ToolEnum.Hide:
            this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Hide);
            break;
          case ToolEnum.Label:
            this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Label);
            break;
          case ToolEnum.Paint:
            this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Paint);
            break;
          case ToolEnum.Isolate:
          case ToolEnum.Fade:
            this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Crosshair);
            break;
          default:
            // Do nothing for now
            break;
        }
      });

    // Chapter animation
    combineLatest(this.selectedChapter$, this.animationPlaying$)
      .pipe(
        filter(([chapter]) => chapter != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([chapter, playing]) => {
        const playCount = get(chapter, 'ui.play.count') || 0;

        if (this.animationService.isChapterAnimated(chapter) && chapter.readyState.animation && playing) {
          this.animationService.play({
            startChapterId: chapter.id,
            loop: (playCount === -1),
            numChapters: 1,
            queue: false // True for higher playcount
          });
        } else {
          this.animationService.pause();
        }
      });

    // display
    this.engineDisplay$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((display) => {
        this.display = display;
        this.changeDetecton.markForCheck();
      });

    // visibility
    this.engineVisible$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((visible) => {
        this.engineVisible = visible;
        this.changeDetecton.markForCheck();
      });

  }
  
  bindUserEvents() {
    // User moves mouse, debounce to small delay so lookup only occurs on pause.
    // Done for performance, doing a node lookup in 3D is expensive.
    this.mouseMove$ = fromEvent<MouseEvent>(this.engineElement.nativeElement, 'mousemove');
    
    // Handle all mouse move events, primarily for panning the camera
    this.mouseMove$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((event) => {
      this.handleMousePan(event);
    });
      
      
    // User moves mouse, debounce to small delay so  hover lookup only occurs on pause.
    // Done for performance, doing a node lookup in 3D is expensive.
    this.mouseMove$.pipe(
      debounceTime(10),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((event) => { 
      this.handleMouseHover(event);
      
    });
  }

  bindKeyEvents() {
    const downKeys = ['w', 'a', 's', 'd', 'e', 'c', '=', '-', '+', '_', 'arrowleft', 'arrowright', 'arrowup', 'arrowdown', 'shiftKey']
      .reduce((result, value) => {
        result[value] = false;
        return result;
      }, {});

    const downKeys$ = new BehaviorSubject<any>(downKeys);

    this.renderer.listen(window, 'keydown', (event) => {
      if (event.repeat || event.metaKey || event.ctrlKey) {
        return;
      }
      
      // Prevent keyboard events while inputs are focused
      const eventNode = event.target.nodeName.toLowerCase();
      if (['input', 'textarea', 'select', 'option'].indexOf(eventNode) >= 0) {
        return;
      }

      downKeys$.next({
        ...downKeys$.value,
        [event.key.toLowerCase()]: true,
        shiftKey: event.shiftKey
      });
    });

    this.renderer.listen(window, 'keyup', (event: KeyboardEvent) => {
      if (event.key) {
        downKeys$.next({
          ...downKeys$.value,
          [event.key.toLowerCase()]: false,
          shiftKey: event.shiftKey
        });
      }
    });
    
    downKeys$
      .pipe(
        // validate input enabled state
        filter(() => this.inputEnabled),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(value => {
        const motion = new CameraMotionModel();
        const zoom = value.shiftKey ? KeyboardCamera.ZoomSlow : KeyboardCamera.Zoom;
        const pan = value.shiftKey ? KeyboardCamera.PanSlow : KeyboardCamera.Pan;
        const rotate = value.shiftKey ? KeyboardCamera.RotateSlow : KeyboardCamera.Rotate;

        motion.zoom += (value.c || value['-'] || value['_']) ? zoom : 0;
        motion.zoom -= (value.e || value['='] || value['+']) ? zoom : 0;

        motion.panX -= value.d ? pan : 0;
        motion.panX += value.a ? pan : 0;

        motion.panY += value.w ? pan : 0;
        motion.panY -= value.s ? pan : 0;

        motion.rotateX -= value.arrowright ? rotate : 0;
        motion.rotateX += value.arrowleft ? rotate : 0;

        motion.rotateY += value.arrowup ? rotate : 0;
        motion.rotateY -= value.arrowdown ? rotate : 0;

        this.store.dispatch(new MoveCamera(motion));
    });
  }

  repositionEngine(position: EnginePositionModel) {
    this.renderer.setStyle(this.engineElement.nativeElement, 'left', `${position.left}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'top', `${position.top}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'width', `${position.width}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'height', `${position.height}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'zIndex', `${position.visibility}`);
    this.renderer.setStyle(
      this.engineElement.nativeElement, 'visibility', (position.visibility === EngineVisibilityEnum.Shown) ? 'visible' : 'hidden'
    );

    // Engine user interaction will be disabled when the engine isn't visible
    this.visible = position.visibility === EngineVisibilityEnum.Shown;
    Human.renderer.resize();
  }

  /**
   * User Interactions
   * Depends on the HammerJS library to ensure touch compatibility, and manage pan handlers (heh...)
   */
  bindHumanInteractions() {
    // Create a HammerJS manager so we can custom define single and double click
    const mc = new Hammer.Manager(this.engineCanvasElement.nativeElement);

    // Tap recognizer with minimal 2 taps
    mc.add(new Hammer.Tap({ event: 'doubletap', taps: 2 }));

    // Single tap recognizer
    mc.add(new Hammer.Tap({ event: 'singletap' }));

    // Pan recognizer
    mc.add(new Hammer.Pan({ event: 'pan', pointers: 0 }));

    // Ping
    mc.add(new Hammer.Pinch());

    // Press
    mc.add(new Hammer.Press({ time: 500 }));

    
    mc.on('singletap', (event) => {
      this.handleTap(event);
    });

    mc.on('doubletap', (event)  => {
      this.handleDoubleTap(event);
    });

    mc.on('press', (event) => {
      this.handlePress(event);
    });

    mc.on('pan', (event) => {
      event.preventDefault();
      this.handlePan(event);
    });

    let cacheScale = 0;
    let cacheX = 0;
    let cacheY = 0;
    let mode = '';
    mc.on('pinchstart', (event) => {
      cacheScale = event.scale;
      cacheX = event.deltaX;
      cacheY = event.deltaY;
      mode = '';
    });

    mc.on('pinchmove', (event) => {
      const panDistanceMet = Math.abs(event.deltaX) > 15 || Math.abs(event.deltaY) > 15;
      const pinchScaleMet = event.scale > 1.1 || event.scale < 0.9;

      // Determine is user is panning or pinching
      if (!mode) {
        mode = panDistanceMet ? 'pan' : 'pinch';
      } else {
        if (mode === 'pan' && pinchScaleMet && !panDistanceMet) {
          mode = 'pinch';
        }

        if (mode === 'pinch' && panDistanceMet && !pinchScaleMet) {
          mode = 'pan';
        }
      }

      const scale = event.scale - cacheScale;
      cacheScale = event.scale;

      switch (mode) {
        case 'pan':
          this.cameraService.setCameraPan(event.velocityX, event.velocityY * TouchCamera.Pinch);
          break;
        case 'pinch':
          Human.renderer.camera.zoom(scale * TouchCamera.Zoom);
          break;
        default:
          this.cameraService.setCameraPan(event.velocityX, event.velocityY * TouchCamera.Pinch);
          Human.renderer.camera.zoom(scale * TouchCamera.Zoom);
      }
    });
  }

  handleMouseHover(event: MouseEvent) {
    if (event.buttons !== MouseButtons.None) {
      return;
    }

    const { offsetX, offsetY} = event;
    const hit = Human.renderer.pick.pick(offsetX, offsetY);

    if (hit.objectId) {
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      const hitNode = this.engineEventsService.getNode(viewGraph, hit.objectId);
      const hoveredNode = this.store.selectSnapshot(EngineNodesState.getHoveredNode);

      if (!hoveredNode || hoveredNode.id !== hitNode.id) {
        this.store.dispatch(new HoverNode(hitNode, hit));
      }
    } else {
      if (this.store.selectSnapshot(EngineNodesState.getHoveredNode) != null) {
        this.store.dispatch(new HoverNode(null));
      }
    }
  }

  handleMousePan(event: MouseEvent) {
    // Left mouse button is handled by HammerJS 'pan'
    if (event.buttons === 2 || event.buttons === 4) {
      let { movementX, movementY } = event;
      movementX /= 20;
      movementY /= 20;

      this.cameraService.setCameraPan(movementX, movementY);
    }
  }

  handleMouseOut() {
    this.store.dispatch(new HoverNode(null));
  }

  // When it comes to wheel events, we account for both trackpad and scroll wheel. The deltaY
  // from these events can vary wildly between trackpad, mouse type, and browser. Read the docs
  // below to learn more.
  // https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent
  handleWheel(event: WheelEvent) {
    let deltaY = 0;

    // Check deltaMode, which determines if we're pixel / line / page scrolling
    switch (event.deltaMode) {
      case 0: // Pixel delta (most common, trackpad and mice scroll wheels in Chrome & Safari)
        
        // Best attempt at determining trackpad vs wheel. 
        // On initial scroll, a trackpad never starts off higher than 10 pixels movement.
        if (this.scrollType === null) {
          this.scrollType = (Math.abs(event.deltaY) < 10) ? 'trackpad' : 'wheel';
        }

        // If trackpad, use deltaY
        // Otherwise use fixed scroll speed
        deltaY = (this.scrollType === 'trackpad') ?
          event.deltaY : 
          Math.sign(event.deltaY) * zoomVelocity;
        break;
      
      default: // Line delta (less common, mice scroll wheels in Firefox)
        this.scrollType = 'wheel';
        break;
    }

    
    // Reset checking tracker. After 500ms of inactivity, reset scroll tracker.
    // Do this so that users transitioning between mouse and trackpad don't have issues.
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.scrollType = null;
    }, 500);

    // Don't allow zoom to exceed max
    if (Math.abs(deltaY) > 40) {
      deltaY = Math.sign(deltaY) * 40;
    }
    

    // For trackpad, use pixel precision.
    if (this.scrollType === 'trackpad') {
      this.cameraService.setCameraZoom(deltaY);
    
    // For wheel, use fixed velocity
    } else {
      this.zoomWithVelocity(deltaY);
    }
  }

  zoomWithVelocity(deltaY) {
    this.cameraService.setCameraZoom(deltaY);

    requestAnimationFrame(() => {
      deltaY *= 0.85;
      if (Math.abs(deltaY) > 1) {
        this.zoomWithVelocity(deltaY);
      }
    });
  }

  handleContextMenu(event: MouseEvent) {
    event.preventDefault();
    const { offsetX, offsetY} = event;
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);
  }

  handleTap(event: HammerInput) {
    const { offsetX, offsetY} = this.getOffset(event);
    
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    this.store.dispatch(new ClickNode(node, event, hit));
  }

  handleDoubleTap(event: HammerInput) { // John Wick approved!
    const { offsetX, offsetY} = this.getOffset(event);
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    if (node) {
      this.store.dispatch(new FlyToObject(node.id));
    }
  }

  handlePress(event: HammerInput) {
    const { offsetX, offsetY} = this.getOffset(event);
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    if (node) {
      this.store.dispatch(new FlyToObject(node.id));
    }
  }
  
  handlePan(event: HammerInput) {
    const panMode = this.store.selectSnapshot(EngineCameraState.getPanMode);

    if (event.srcEvent.shiftKey || panMode === 'pan') {
      let { movementX, movementY } = event.srcEvent as PointerEvent;
      movementX /= 20;
      movementY /= 20;
      this.cameraService.setCameraPan(movementX, movementY);

    } else {
      this.cameraService.setCameraRotation(event.velocityX * TouchCamera.Rotate, event.velocityY * TouchCamera.Rotate);
    }
  }

  handleMouseInputEngineTick() {
    const rotation = this.cameraService.tickCameraRotation();
    const pan = this.cameraService.tickCameraPan();
    const zoom = this.cameraService.tickCameraZoom();

    // Rotation
    if (rotation.vx || rotation.vy) {
      this.cameraService.rotateCameraStatic(
        rotation.vx !== 0 ? rotation.vx * MouseCamera.Rotate : 0,
        rotation.vy !== 0 ? rotation.vy * MouseCamera.Rotate : 0
      );
    }

    // Pan
    if (pan.x || pan.y) {
      const f = Math.max(Human.renderer.camera.lookDistance / 80, 0.01);
      this.cameraService.panCameraStatic(
        (pan.x * f) * MouseCamera.Pan,
        (pan.y * f) * MouseCamera.Pan,
        (pan.z * f) * MouseCamera.Pan
      );
    }

    // Zoom
    if (zoom !== 0) {
      this.cameraService.setHumanZoom(zoom * MouseCamera.Zoom);
    }
  }




  handleCameraTick() {
    const motion = this.store.selectSnapshot(EngineCameraState.getCameraMotion);

    // PAN
    this.cameraService.setCameraPan(motion.panX, motion.panY);
    this.cameraService.setCameraRotation(motion.rotateX, motion.rotateY);
    this.cameraService.setCameraZoom(motion.zoom);
  }


  flyToObject(objectId: string, animationDuration?: number) {
    Human.renderer.camera.fly.flyTo({
      objectId,
      animationDuration
    });
  }
  
  jumpToObject(objectId: string) {
    Human.renderer.camera.fly.jumpTo({ objectId });
  }

  flyToPoint(destination: CameraModel, animationDuration?: number) {
    Human.renderer.camera.fly.flyTo({
      ...destination,
      animationDuration 
    });
  }

  jumpToPoint(destination: CameraModel) {
    Human.renderer.camera.fly.jumpTo(destination);
  }

  getNodeFromHit(hit: HitModel) {
    let node: ViewTreeNode;

    if (hit.objectId) {
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      node = this.engineEventsService.getNode(viewGraph, hit.objectId);
    }

    return node;
  }

  getHit(x, y) {
    // For clicks, set rayPick to true so we get a completely detailed hit object
    // that includes the exact triangle the user clicked on. This is different 
    // from hover, that only target the general node. 
    const hit = Human.renderer.pick.pick(x, y, {rayPick: true }) as HitModel;
    return hit;
  }

  getContainerStyles() {
    const styles = {};

    // set background
    if (this.background && !this.isTransparentBackground) {
      styles['background-image'] = BackgroundModel.toCSS(this.background);
    }
    // set display
    if (!this.display) {
      styles['display'] = 'none';
    }
    // set transparent
    if (!this.engineVisible) {
      styles['opacity'] = 0;
    }

    return styles;
  }

  // We calculate offsetX and offsetY using rect and center because iOS safari
  // does not natively support offsetX and offsetY. Those properties return `undefined`
  // when requested. 
  // More: http://www.jacklmoore.com/notes/mouse-position/
  getOffset(event: any): {offsetX: number, offsetY: number} {
    let x = 0;
    let y = 0;

    if (event.center) {
      x = event.center.x;
      y = event.center.y;
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    const target = <HTMLDivElement>this.engineElement.nativeElement;
    const rect = target.getBoundingClientRect();
    
    return {
      offsetX: x - rect.left,
      offsetY: y - rect.top
    };
  }

  private calculateSelectedObjectsUpdate(previouslySelected: ViewTreeNode[], currentlySelected: ViewTreeNode[]): { [key: string]: boolean } {
    const previousSelectedState = this.createNodeStateMap(previouslySelected, false);
    const currentlySelectedState = this.createNodeStateMap(currentlySelected, true);

    return {
      ...previousSelectedState,
      ...currentlySelectedState
    };
  }

  private createNodeStateMap(nodes: ViewTreeNode[], state: boolean): { [key: string]: boolean } {
    return nodes.reduce((acc, node) => {
      acc[node.id] = state;
      return acc;
    }, {});
  }
}
